import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  Link,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import EditIcon from "@material-ui/icons/Edit";

import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import Navbar from "components/Navbar";
import Heading from "components/Heading";
import FindBookingModal from "components/FindBookingModal";
import FindBookingBox from "components/FindBookingBox";
import BookingReservationsCard from "components/BookingReservationsCard";
import CommunicationPreferences from "components/CommunicationPreferences";
import { fetchProfile, setProfile } from "features/user/userSlice";
import { setRoomDetails } from "features/room/roomSlice";
import api from "utils/api";
import { PROFILE_SECTIONS } from "utils/constants";
import { generateFirstName } from "utils/helpers";
import MemberTag from "components/MemberTag";
import { useSignOutUser } from "hooks";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "inherit",
  },
  contentRow: {
    width: "90%",
    margin: "auto",
    [theme.breakpoints.up(theme.breakpoints.values.desktop)]: {
      width: "80%",
      maxWidth: "1300px",
    },
  },
  reservationForm: {
    justifyContent: "center",
  },
  reservationDetails: {
    margin: "20px 300px",
  },
  reservationActions: {
    margin: "20px 300px",
  },
  drawerPaper: {
    position: "static",
    background: "transparent",
    borderRight: "none",
  },
  drawerSelected: {
    background: theme.palette.grey[200],
    color: theme.palette.text.onDark.primary,
    borderBottom: "2px solid " + theme.palette.primary.main,
    [theme.breakpoints.up(theme.breakpoints.values.tablet)]: {
      borderBottom: "none",
    },
  },
  drawerItem: {
    cursor: "pointer",
    padding: "1rem",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.up(theme.breakpoints.values.tablet)]: {
      flexDirection: "row",
    },
  },
  drawerList: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    paddingTop: "0",
    marginBottom: "1rem",
    flexWrap: "nowrap",
    [theme.breakpoints.up(theme.breakpoints.values.tablet)]: {
      flexDirection: "column",
      paddingTop: "8px",
      maxWidth: "300px",
    },
  },
  formContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "1rem",
  },
  marketingSection: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  detailsHeader: {
    margin: "3rem 0",
    height: "30px",
  },
  editIcon: {
    marginRight: "10px",
  },
  detailsContainer: {
    width: "90%",
    [theme.breakpoints.up(theme.breakpoints.values.tablet)]: {
      width: "50%",
    },
  },
  profileSectionButton: {
    width: "100%",
    padding: 0,
  },
  memberTag: {
    marginTop: "0px",
    marginBottom: "0px",
    maxWidth: "fit-content",
    [theme.breakpoints.up(theme.breakpoints.values.tablet)]: {
      marginTop: "12px",
      marginBottom: "8px",
    },
  },
}));

const Account = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const tabletUpScreen = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.tablet));
  const dispatch = useDispatch();
  const signOutUser = useSignOutUser();
  const authUser = useSelector((state) => state.user.authUser);
  const profile = useSelector((state) => state.user.profile);
  const firebaseToken = useSelector((state) => state.user.firebaseToken);
  const location = useLocation();
  const MOLLIE_MEMBER_TITLE = process.env.REACT_APP_MOLLIES_MEMBER_TITLE;

  const [groupedBookings, setGroupedBookings] = useState({});
  const [showDetailsSection, setShowDetailsSection] = useState(props.showDetails);
  const [loading, setLoading] = useState(true);
  const [editDetails, setEditDetails] = useState(false);
  const [details, setDetails] = useState();
  const [findBookingModalOpen, setFindBookingModalOpen] = useState(false);

  const setReservationsGroupedByBooking = () => {
    let groupedBookings = {};
    profile.apaleoReservations.forEach((reservation) => {
      if (groupedBookings[reservation.bookingId]) {
        groupedBookings[reservation.bookingId] = [
          ...groupedBookings[reservation.bookingId],
          reservation,
        ];
      } else {
        groupedBookings[reservation.bookingId] = [reservation];
      }
    });
    setGroupedBookings(groupedBookings);
  };

  const fetchLatestReservationsOnProfile = async (wait) => {
    if (authUser && firebaseToken) {
      if (wait) {
        await dispatch(fetchProfile(firebaseToken));
        setLoading(false);
      } else {
        dispatch(fetchProfile(firebaseToken));
      }
    }
  };

  const handleDetailsChange = (event) => {
    setDetails({
      ...details,
      [event.target.name]: event.target.value,
    });
  };

  const handleDetailsSubmit = () => {
    api
      .updateProfile(firebaseToken, {
        first_name: details.firstName,
        last_name: details.lastName,
      })
      .then((res) => {
        dispatch(
          setProfile({
            ...profile,
            firstName: details.firstName,
            lastName: details.lastName,
          }),
        );
        props.onNotifOpen("Profile updated successfully!", {
          variant: "success",
        });
      })
      .catch(() => {
        props.onNotifOpen("Something went wrong.", {
          variant: "error",
        });
      });
  };

  const handleReservationCancelled = () => {
    fetchLatestReservationsOnProfile(false);
  };

  const handleUserSignOut = () => {
    signOutUser();
  };

  useEffect(() => {
    if (!authUser) {
      props.redirectAuth(location.pathname);
    }
  }, [authUser]);

  useEffect(() => {
    if (firebaseToken) {
      fetchLatestReservationsOnProfile(true);
    }
  }, [firebaseToken]);

  useEffect(() => {
    if (profile && profile.apaleoReservations) {
      setReservationsGroupedByBooking();
    }
  }, [profile]);

  useEffect(() => {
    api.getRoomDetails().then((res) => {
      dispatch(setRoomDetails(res.data));
    });
  }, []);

  useEffect(() => {
    setDetails({
      firstName: profile?.firstName,
      lastName: profile?.lastName,
    });
  }, [profile]);

  return (
    <div className={classes.root}>
      <Navbar withProfile onProfileChange={props.onProfileChange} onNotifOpen={props.onNotifOpen} />
      <FindBookingModal
        open={findBookingModalOpen}
        setOpen={setFindBookingModalOpen}
        onNotifOpen={props.onNotifOpen}
      />
      <Grid container className={classes.contentRow}>
        <Box mt={4}>
          <Grid container justifyContent="flex-start" alignItems="center" hidden={!tabletUpScreen}>
            <Typography variant="body2" color="textSecondary">
              My account
            </Typography>
            <Box mx={2}>
              <Typography variant="body2" color="textSecondary">
                /
              </Typography>
            </Box>
            <Typography variant="body2" color="textPrimary">
              My {showDetailsSection}
            </Typography>
          </Grid>
        </Box>
        <Heading titleText="My account" responsive={"tablet"}>
          {!tabletUpScreen && authUser && authUser.emailVerified && (
            <MemberTag
              label={MOLLIE_MEMBER_TITLE}
              className={classes.memberTag}
              tagVariant="body2"
            />
          )}
        </Heading>
        <Grid container direction={tabletUpScreen ? "row" : "column"}>
          {authUser ? (
            <Drawer
              variant="permanent"
              anchor="left"
              PaperProps={{ className: classes.drawerPaper }}>
              {tabletUpScreen ? (
                <Box mb={2}>
                  <Typography variant="h6" color="textPrimary">
                    Hello {generateFirstName(authUser?.displayName)}
                  </Typography>
                  {authUser.emailVerified && (
                    <MemberTag
                      label={MOLLIE_MEMBER_TITLE}
                      className={classes.memberTag}
                      tagVariant="body2"
                    />
                  )}
                  <Link
                    component="button"
                    variant="body2"
                    color="textSecondary"
                    onClick={handleUserSignOut}>
                    Not you? Sign Out
                  </Link>
                </Box>
              ) : null}
              {tabletUpScreen ? <Divider /> : null}
              <Grid container className={classes.drawerList}>
                <Button
                  onClick={() => {
                    setShowDetailsSection(PROFILE_SECTIONS.DETAILS);
                  }}
                  className={classes.profileSectionButton}>
                  <Grid
                    container
                    className={`${classes.drawerItem} ${
                      showDetailsSection === PROFILE_SECTIONS.DETAILS
                        ? classes.drawerSelected
                        : null
                    }`}>
                    <PersonOutlineOutlinedIcon />
                    {tabletUpScreen && (
                      <Box mr={tabletUpScreen ? 6 : 3} ml={tabletUpScreen ? 2 : 3} my={1}>
                        <Typography variant="subtitle2">My details</Typography>
                      </Box>
                    )}
                  </Grid>
                </Button>
                <Button
                  onClick={() => {
                    setShowDetailsSection(PROFILE_SECTIONS.BOOKINGS);
                  }}
                  className={classes.profileSectionButton}>
                  <Grid
                    container
                    className={`${classes.drawerItem} ${
                      showDetailsSection === PROFILE_SECTIONS.BOOKINGS
                        ? classes.drawerSelected
                        : null
                    }`}>
                    <CalendarTodayIcon />
                    {tabletUpScreen && (
                      <Box mr={tabletUpScreen ? 6 : 3} ml={tabletUpScreen ? 2 : 3} my={1}>
                        <Typography variant="subtitle2">My bookings</Typography>
                      </Box>
                    )}
                  </Grid>
                </Button>
                {tabletUpScreen ? (
                  <FindBookingBox
                    hidden={showDetailsSection !== PROFILE_SECTIONS.BOOKINGS}
                    setOpen={setFindBookingModalOpen}
                  />
                ) : null}
                <Button
                  onClick={() => {
                    setShowDetailsSection(PROFILE_SECTIONS.PREFERENCES);
                  }}
                  className={classes.profileSectionButton}>
                  <Grid
                    container
                    className={`${classes.drawerItem} ${
                      showDetailsSection === PROFILE_SECTIONS.PREFERENCES
                        ? classes.drawerSelected
                        : null
                    }`}>
                    <SettingsOutlinedIcon />
                    {tabletUpScreen && (
                      <Box mr={tabletUpScreen ? 6 : 3} ml={tabletUpScreen ? 2 : 3} my={1}>
                        <Typography variant="subtitle2">Preferences</Typography>
                      </Box>
                    )}
                  </Grid>
                </Button>
              </Grid>
            </Drawer>
          ) : null}
          {showDetailsSection === PROFILE_SECTIONS.DETAILS && (
            <Box className={classes.detailsContainer} m="auto">
              <Grid
                container
                justifyContent="space-between"
                alignItems="flex-end"
                className={classes.detailsHeader}>
                <Typography variant="h5" color="textPrimary">
                  Personal details
                </Typography>
                {!editDetails ? (
                  <Box alignSelf="flex-start">
                    <Button
                      color="primary"
                      onClick={() => {
                        setEditDetails(true);
                      }}>
                      <EditIcon className={classes.editIcon} />
                      Edit
                    </Button>
                  </Box>
                ) : null}
              </Grid>

              <div className={classes.formContainer}>
                <Typography variant="subtitle2" color="textPrimary">
                  Email
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  {profile?.email}
                </Typography>

                <Typography variant="subtitle2" color="textPrimary">
                  First name
                </Typography>
                {editDetails ? (
                  <TextField
                    value={details.firstName}
                    name="firstName"
                    onChange={handleDetailsChange}
                  />
                ) : (
                  <Typography variant="body2" color="textPrimary">
                    {profile?.firstName}
                  </Typography>
                )}

                <Typography variant="subtitle2" color="textPrimary">
                  Last name
                </Typography>
                {editDetails ? (
                  <TextField
                    value={details.lastName}
                    name="lastName"
                    onChange={handleDetailsChange}
                  />
                ) : (
                  <Typography variant="body2" color="textPrimary">
                    {profile?.lastName}
                  </Typography>
                )}
              </div>
              {editDetails ? (
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Box mr={1}>
                    <Button
                      onClick={() => {
                        setEditDetails(false);
                      }}
                      variant="outlined"
                      color="primary">
                      Cancel
                    </Button>
                  </Box>
                  <Button onClick={handleDetailsSubmit} variant="contained" color="primary">
                    Save changes
                  </Button>
                </Box>
              ) : null}
            </Box>
          )}
          {showDetailsSection === PROFILE_SECTIONS.BOOKINGS && (
            <Box m="auto">
              {loading ? <LoadingSpinner loading={loading} /> : null}
              {Object.keys(groupedBookings).length === 0 && !loading ? (
                <Typography variant="body2" color="textPrimary" align="center">
                  No reservations.
                </Typography>
              ) : null}
              {!loading
                ? Object.keys(groupedBookings).map((bookingId, i) => (
                    <BookingReservationsCard
                      key={i}
                      bookingReservations={groupedBookings[bookingId]}
                      onReservationCancelled={handleReservationCancelled}
                      onNotifOpen={props.onNotifOpen}
                    />
                  ))
                : null}
              {tabletUpScreen ? null : (
                <Box mb={4}>
                  <FindBookingBox
                    hidden={showDetailsSection === PROFILE_SECTIONS.BOOKINGS}
                    setOpen={setFindBookingModalOpen}
                  />
                </Box>
              )}
            </Box>
          )}
          {showDetailsSection === PROFILE_SECTIONS.PREFERENCES && (
            <Box className={classes.detailsContainer} m="auto">
              <CommunicationPreferences onNotifOpen={props.onNotifOpen} />
            </Box>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

Account.propTypes = {
  showDetails: PropTypes.string,
  onNotifOpen: PropTypes.func,
  redirectAuth: PropTypes.func,
  onProfileChange: PropTypes.func,
};

export default Account;
